/**
 * FASE 4 — FICHA DO ATLETA (para imprimir).
 *
 * A ideia é fechar o ciclo com a ficha de papel: a tela mostra TUDO que o
 * sistema tem do atleta (dados pessoais, documentos, responsável, endereço,
 * escola, saúde, graduação e vínculo) em uma folha BRANCA — que é o que se
 * imprime sem gastar tinta e o que se lê no papel.
 *
 * Ao imprimir, a navegação do sistema é escondida (classe no `body`) e sai só a
 * ficha. O QR é o mesmo da carteirinha.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { athleteApi, fileApi, minhaEquipeApi } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Printer } from 'lucide-react';
import { formatarDataBr, tempoNaFaixa, ordenarGraduacoes } from '../lib/graduacao';
// O CPF é gravado só com dígitos; na ficha impressa ele sai formatado.
import { formatCpf } from '../lib/documentos';
// Rotina da equipe (turmas, frequência e mensalidades) — só para quem tem acesso
import { competenciaTexto, formatarValor, rotuloSituacao } from '../lib/minhaEquipe';

const vazio = (valor) => {
  if (valor === null || valor === undefined) return '—';
  const texto = String(valor).trim();
  return texto === '' ? '—' : texto;
};

/** Linha do tipo "Rótulo: valor" da ficha. */
function Linha({ rotulo, valor, largo = false }) {
  return (
    <div className={largo ? 'col-span-2' : ''}>
      <div className="text-micro uppercase tracking-wider text-gray-500">{rotulo}</div>
      <div className="text-xs text-black font-medium break-words">{vazio(valor)}</div>
    </div>
  );
}

function Secao({ titulo, children }) {
  return (
    <section className="mt-4 break-inside-avoid">
      <h2 className="text-micro font-bold uppercase tracking-wider text-black border-b border-gray-400 pb-0.5 mb-2">
        {titulo}
      </h2>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">{children}</div>
    </section>
  );
}

export default function AthleteFichaPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [rotina, setRotina] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Enquanto esta página está aberta, a impressão esconde a navegação.
    document.body.classList.add('imprimindo-ficha');
    return () => document.body.classList.remove('imprimindo-ficha');
  }, []);

  useEffect(() => {
    const carregar = async () => {
      try {
        const [card, grads] = await Promise.all([
          athleteApi.getCard(id),
          athleteApi.graduations(id).catch(() => ({ data: { graduations: [] } })),
        ]);
        setData(card.data);
        setHistorico(ordenarGraduacoes(grads.data?.graduations || []));
      } catch (error) {
        toast.error('Erro ao carregar a ficha');
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [id]);

  useEffect(() => {
    // A rotina é do módulo "Minha Equipe": quem não tem acesso recebe 403 e o
    // bloco simplesmente não aparece (a ficha continua funcionando).
    minhaEquipeApi.rotina(id)
      .then((r) => setRotina(r.data))
      .catch(() => setRotina(null));
  }, [id]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-bold text-white">Atleta não encontrado</h2>
          <Link to="/athletes" className="text-[#D4AF37] underline text-sm">Voltar</Link>
        </div>
      </MainLayout>
    );
  }

  const { athlete, team, professor_name: professorName } = data;
  const foto = athlete.photo_url || (athlete.photo_path ? fileApi.getUrl(athlete.photo_path) : null);

  return (
    <MainLayout>
      {/* Barra de ações — NÃO sai na impressão */}
      <div className="flex items-center justify-between mb-4 print-hide" data-testid="ficha-acoes">
        <Link to="/athletes" className="text-sm text-[#A1A1AA] hover:text-white flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Voltar para atletas
        </Link>
        <div className="flex gap-2">
          <Link to={`/athletes/${id}/card`}>
            <Button variant="outline" className="border-[#27272A] text-[#A1A1AA]">Carteirinha</Button>
          </Link>
          <Button
            onClick={() => window.print()}
            data-testid="ficha-imprimir"
            className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
          >
            <Printer className="w-4 h-4" /> Imprimir
          </Button>
        </div>
      </div>

      {/* ===== A FOLHA ===== */}
      <div
        className="escala-original mx-auto bg-white text-black p-8 rounded-lg shadow-lg"
        style={{ maxWidth: '210mm', minHeight: '270mm' }}
        data-testid="ficha-folha"
      >
        {/* Cabeçalho */}
        <header className="flex items-start gap-5 border-b-2 border-black pb-3">
          {foto ? (
            <img
              src={foto}
              alt={athlete.name}
              className="w-24 h-28 object-cover border border-gray-400"
              data-testid="ficha-foto"
            />
          ) : (
            <div className="w-24 h-28 border border-dashed border-gray-400 flex items-center justify-center text-gray-400 text-micro text-center">
              sem foto
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-lg font-bold uppercase">Ficha do Atleta</h1>
            <p className="text-[13px] font-semibold mt-1">{vazio(athlete.name)}</p>
            <p className="text-micro text-gray-700">
              {vazio(team?.name)} · Faixa {vazio(athlete.belt)}
              {athlete.belt_since ? ` · desde ${formatarDataBr(athlete.belt_since)}` : ''}
            </p>
            <p className="text-micro text-gray-600 mt-1">
              {vazio(athlete.age_class)}{athlete.age ? ` · ${athlete.age} anos` : ''}
            </p>
          </div>
          <img
            src={athleteApi.getQRCode(id)}
            alt="QR do atleta"
            className="w-20 h-20"
            data-testid="ficha-qr"
          />
        </header>

        <Secao titulo="Dados pessoais">
          <Linha rotulo="Nome completo" valor={athlete.name} largo />
          <Linha rotulo="Data de nascimento" valor={formatarDataBr(athlete.birth_date)} />
          <Linha rotulo="Sexo" valor={athlete.sex === 'F' ? 'Feminino' : 'Masculino'} />
          <Linha rotulo="Nacionalidade" valor={athlete.nationality} />
          <Linha rotulo="Naturalidade" valor={athlete.birth_place} />
          <Linha rotulo="Peso" valor={athlete.weight ? `${athlete.weight} kg` : ''} />
          <Linha rotulo="Altura" valor={athlete.height_cm ? `${athlete.height_cm} cm` : ''} />
          <Linha rotulo="Tipo sanguíneo" valor={athlete.blood_type} />
        </Secao>

        <Secao titulo="Documentos e contato">
          {/* DOCUMENTO É UM CAMPO SÓ (decisão da Fase E): o cadastro guarda o CPF
              **ou** o RG e o campo `document` é o espelho que a carteirinha e a
              busca usam. Não existem linhas separadas de "CPF" e "RG" — se o
              espelho estiver vazio (dado antigo), a ficha deriva do que houver. */}
          <Linha
            rotulo="Documento (CPF/RG)"
            valor={athlete.document || (athlete.cpf ? formatCpf(athlete.cpf) : athlete.rg)}
            largo
          />
          <Linha rotulo="Telefone" valor={athlete.phone} />
          <Linha rotulo="E-mail" valor={athlete.email} />
          <Linha rotulo="Tel. residencial" valor={athlete.home_phone} />
          <Linha rotulo="Celular do responsável" valor={athlete.responsible_cell_phone} />
        </Secao>

        <Secao titulo="Responsável legal">
          <Linha rotulo="Nome" valor={athlete.guardian_name} />
          <Linha rotulo="Parentesco" valor={athlete.guardian_relationship} />
          <Linha rotulo="CPF do responsável" valor={athlete.guardian_cpf} />
          <Linha rotulo="Pai" valor={athlete.father_name} />
          <Linha rotulo="Mãe" valor={athlete.mother_name} />
        </Secao>

        <Secao titulo="Endereço">
          <Linha rotulo="Endereço" valor={athlete.address} largo />
          <Linha rotulo="Bairro" valor={athlete.neighborhood} />
          <Linha rotulo="CEP" valor={athlete.zip_code} />
          <Linha rotulo="Cidade" valor={athlete.city} />
          <Linha rotulo="Estado" valor={athlete.state} />
        </Secao>

        <Secao titulo="Escola">
          <Linha rotulo="Escolaridade" valor={athlete.education_level} />
          <Linha rotulo="Escola" valor={athlete.school_name} />
          <Linha rotulo="Série" valor={athlete.school_grade} />
          <Linha rotulo="Turno" valor={athlete.school_shift} />
        </Secao>

        <Secao titulo="Saúde">
          <Linha
            rotulo="Necessidades especiais"
            valor={athlete.has_special_needs ? `Sim — ${vazio(athlete.special_needs_description)}` : 'Não'}
            largo
          />
          <Linha
            rotulo="Problema de saúde"
            valor={athlete.has_health_issues ? `Sim — ${vazio(athlete.health_issues_description)}` : 'Não'}
            largo
          />
          <Linha rotulo="Atestado médico" valor={formatarDataBr(athlete.medical_certificate_date)} />
          <Linha rotulo="Médico / CRM" valor={[athlete.doctor_name, athlete.doctor_crm].filter(Boolean).join(' · ')} />
        </Secao>

        <Secao titulo="Graduação">
          <Linha rotulo="Faixa atual" valor={athlete.belt} />
          <Linha rotulo="Desde" valor={formatarDataBr(athlete.belt_since)} />
          <Linha
            rotulo="Tempo na faixa"
            valor={athlete.belt_since ? tempoNaFaixa(athlete.belt_since) : ''}
          />
          <Linha rotulo="Histórico" valor={historico.length ? `${historico.length} registro(s)` : 'sem histórico'} />
          {historico.length > 0 && (
            <div className="col-span-2">
              <table className="w-full text-micro mt-1" data-testid="ficha-historico">
                <thead>
                  <tr className="text-left text-gray-500 text-micro uppercase">
                    <th className="border-b border-gray-300 py-0.5">Data</th>
                    <th className="border-b border-gray-300 py-0.5">Faixa</th>
                    <th className="border-b border-gray-300 py-0.5">Quem graduou</th>
                    <th className="border-b border-gray-300 py-0.5">Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map((g) => (
                    <tr key={g.id}>
                      <td className="py-0.5">{formatarDataBr(g.date)}</td>
                      <td className="py-0.5">{vazio(g.belt)}</td>
                      <td className="py-0.5">{vazio(g.professor_name)}</td>
                      <td className="py-0.5">{vazio(g.notes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Secao>

        <Secao titulo="Vínculo">
          <Linha rotulo="Equipe" valor={team?.name} />
          <Linha rotulo="Professor (sensei)" valor={professorName} />
          <Linha rotulo="Início no Jiu-Jitsu" valor={formatarDataBr(athlete.start_date)} />
          <Linha rotulo="ID do atleta" valor={athlete._id} />
        </Secao>

        {/* MINI GERENCIAMENTO — ROTINA (turmas, frequência e mensalidade).
            Só aparece para quem tem acesso ao módulo (super_admin, professor e
            equipe); para os outros papéis a chamada falha e o bloco não sai. */}
        {rotina && (
          <Secao titulo="Rotina na equipe">
            <div className="col-span-2 grid grid-cols-2 gap-x-6 gap-y-2">
              <Linha
                rotulo="Turmas"
                valor={rotina.turmas?.length
                  ? rotina.turmas.map((t) => t.name).join(' · ')
                  : 'sem turma'}
                largo
              />
              <Linha
                rotulo="Frequência"
                valor={rotina.frequencia?.aulas
                  ? `${rotina.frequencia.aulas} aula(s) · ${rotina.frequencia.presencas} presença(s) · ${rotina.frequencia.faltas} falta(s) · ${rotina.frequencia.percentual}%`
                  : 'sem chamada registrada'}
                largo
              />
              {rotina.mensalidades?.length > 0 && (
                <div className="col-span-2">
                  <div className="text-micro uppercase tracking-wider text-gray-500">Últimas mensalidades</div>
                  <table className="w-full text-micro mt-1" data-testid="ficha-mensalidades">
                    <thead>
                      <tr className="text-left text-gray-500 text-micro uppercase">
                        <th className="border-b border-gray-300 py-0.5">Competência</th>
                        <th className="border-b border-gray-300 py-0.5">Valor</th>
                        <th className="border-b border-gray-300 py-0.5">Vencimento</th>
                        <th className="border-b border-gray-300 py-0.5">Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rotina.mensalidades.slice(0, 6).map((m) => (
                        <tr key={m.id}>
                          <td className="py-0.5">{competenciaTexto(m.competence)}</td>
                          <td className="py-0.5">{formatarValor(m.amount)}</td>
                          <td className="py-0.5">{formatarDataBr(m.due_date)}</td>
                          <td className="py-0.5">{rotuloSituacao(m.situacao)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Secao>
        )}

        {/* Assinaturas */}
        <div className="mt-10 grid grid-cols-2 gap-10 text-micro text-gray-700">
          <div className="border-t border-gray-500 pt-1 text-center">Assinatura do atleta</div>
          <div className="border-t border-gray-500 pt-1 text-center">Assinatura do responsável</div>
        </div>
        <p className="text-micro text-gray-500 mt-4 text-center">
          Documento gerado pelo sistema e-Torneios em {formatarDataBr(new Date().toISOString())}.
        </p>
      </div>
    </MainLayout>
  );
}
